import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Plus,
  Eye,
  Trash2,
  FileSpreadsheet,
  Loader2,
  Filter,
  X,
  PackageOpen,
  Package,
  Trophy,
  BadgeX,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ClockArrowUp,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { clientService } from "../../services/clientService";
import { leadService } from "../../services/leadService";

const getLeadDisplayName = (l) => l?.lead?.name || "—";
const getLeadDisplayCompanyName = (l) => l?.lead?.company_name || "—";
const getLeadDisplayService = (l) => l?.lead?.services?.[0] || "—";

const SERVICE_TAG_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-purple-100 text-purple-700",
  "bg-green-100 text-green-700",
  "bg-orange-100 text-orange-700",
  "bg-pink-100 text-pink-700",
  "bg-teal-100 text-teal-700",
  "bg-indigo-100 text-indigo-700",
  "bg-yellow-100 text-yellow-800",
];

const getServiceTagColor = (service) => {
  let hash = 0;
  for (let i = 0; i < service.length; i++) {
    hash = (hash * 31 + service.charCodeAt(i)) & 0xffff;
  }
  return SERVICE_TAG_COLORS[hash % SERVICE_TAG_COLORS.length];
};

const ServiceTags = ({ lead }) => {
  const services = lead?.lead?.services;
  if (!services?.length)
    return <span className="text-canopy-textMuted">—</span>;
  return (
    <div className="flex flex-col gap-1">
      {services.map((service) => (
        <span
          key={service}
          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getServiceTagColor(service)}`}
        >
          {service}
        </span>
      ))}
    </div>
  );
};
const STATUS_CLASSES = {
  Open: "bg-blue-100 text-blue-700",
  "Waiting for Documents": "bg-yellow-100 text-yellow-800",
  "Ready for Quote": "bg-purple-100 text-purple-700",
  "Waiting on Acceptance": "bg-orange-100 text-orange-700",
  Closed: "bg-green-100 text-green-700",
  Lost: "bg-red-100 text-red-700",
};

const StatusTag = ({ lead }) => {
  const status = lead?.lead?.status;
  if (!status) return <span className="text-canopy-textMuted">—</span>;
  const colorClass = STATUS_CLASSES[status] || "bg-gray-100 text-gray-700";
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
    >
      {status}
    </span>
  );
};

const URGENCY_CLASSES = {
  High: "bg-purple-900 text-white",
  Medium: "bg-purple-500 text-white",
  Low: "bg-purple-100 text-purple-800",
};

const UrgencyTag = ({ lead }) => {
  const urgency = lead?.lead?.urgency;
  if (!urgency) return <span className="text-canopy-textMuted">—</span>;
  const colorClass = URGENCY_CLASSES[urgency] || "bg-gray-100 text-gray-700";
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
    >
      {urgency}
    </span>
  );
};

const URGENCY_RANK = { High: 0, Medium: 1, Low: 2 };

const STATUS_RANK = {
  Open: 0,
  "Waiting for Documents": 1,
  "Ready for Quote": 2,
  "Waiting on Acceptance": 3,
  Closed: 4,
  Lost: 5,
};

const getLeadDisplayStatus = (l) => l?.lead?.status || "—";
const getLeadDisplayEmail = (l) => l?.lead?.email || "—";
const getLeadDisplayPhone = (l) => l?.lead?.phone || "—";
const getLeadDisplayUrgency = (l) => l?.lead?.urgency || "—";
const getLeadDisplaySalesRep = (l) => l?.lead?.sales_rep_name || "—";

const normalizeCellValue = (value) => {
  if (value === null || value === undefined) return "—";
  const text = String(value).trim();
  return text.length > 0 ? text : "—";
};
const getLeadStatusValue = (l) =>
  normalizeCellValue(l?.lead?.status || l?.status);
const getLeadCreationDate = (l) => l?.lead?.creation_date || null;

const toValidDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getMonthOffsetFromNow = (date, now = new Date()) =>
  (now.getFullYear() - date.getFullYear()) * 12 +
  (now.getMonth() - date.getMonth());

const getAntiquityMonthLabel = (offset) => {
  if (offset === 0) return "Current month";
  if (offset === 1) return "1 month ago";
  return `${offset} months ago`;
};

const FILTERABLE_COLUMNS = [
  {
    key: "name",
    label: "Name",
    accessor: (lead) => normalizeCellValue(getLeadDisplayName(lead)),
  },
  {
    key: "company",
    label: "Company / Contact",
    accessor: (lead) => normalizeCellValue(getLeadDisplayCompanyName(lead)),
  },
  {
    key: "services",
    label: "Services",
    accessor: (lead) => normalizeCellValue(getLeadDisplayService(lead)),
  },
  {
    key: "status",
    label: "Status",
    accessor: (lead) => normalizeCellValue(getLeadDisplayStatus(lead)),
  },
  {
    key: "email",
    label: "Email",
    accessor: (lead) => normalizeCellValue(getLeadDisplayEmail(lead)),
  },
  {
    key: "phone",
    label: "Phone",
    accessor: (lead) => normalizeCellValue(getLeadDisplayPhone(lead)),
  },
  {
    key: "urgency",
    label: "Urgency",
    accessor: (lead) => normalizeCellValue(getLeadDisplayUrgency(lead)),
  },
  {
    key: "salesRep",
    label: "Sales Rep",
    accessor: (lead) => normalizeCellValue(getLeadDisplaySalesRep(lead)),
  },
];

const FILTERABLE_COLUMN_MAP = FILTERABLE_COLUMNS.reduce((acc, column) => {
  acc[column.key] = column;
  return acc;
}, {});

const ColumnFilter = ({
  columnKey,
  label,
  options = [],
  selectedValues,
  onApply,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [draftSelection, setDraftSelection] = useState(new Set());
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const selectAllRef = useRef(null);
  const isActive = Array.isArray(selectedValues);
  const alignLeft = columnKey === "name";

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: alignLeft ? rect.left : rect.right - 256,
      });
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const baseSelection =
      selectedValues && selectedValues.length > 0 ? selectedValues : options;
    setDraftSelection(new Set(baseSelection));
    setSearchTerm("");
  }, [isOpen, options, selectedValues]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      options.length > 0 &&
      draftSelection.size > 0 &&
      draftSelection.size < options.length;
  }, [draftSelection, options]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const matcher = searchTerm.trim().toLowerCase();
    return options.filter((option) => option.toLowerCase().includes(matcher));
  }, [options, searchTerm]);

  const toggleOption = (value) => {
    setDraftSelection((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (draftSelection.size === options.length) {
      setDraftSelection(new Set());
      return;
    }
    setDraftSelection(new Set(options));
  };

  const handleApply = () => {
    if (options.length === 0 || draftSelection.size === options.length) {
      onApply(columnKey, null);
    } else {
      onApply(columnKey, Array.from(draftSelection));
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    onApply(columnKey, null);
    setDraftSelection(new Set(options));
    setIsOpen(false);
  };

  const allSelected =
    options.length > 0 && draftSelection.size === options.length;

  return (
    <div className="inline-flex items-center">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={`Filter ${label}`}
        className={`p-1 rounded border border-transparent hover:border-canopy-border transition-colors ${
          isActive ? "text-canopy-blue" : "text-canopy-textMuted"
        }`}
        onClick={handleToggle}
      >
        <Filter className="h-4 w-4" />
        <span className="sr-only">Filter {label}</span>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: dropdownPos.top,
              left: dropdownPos.left,
              zIndex: 9999,
              width: 256,
            }}
            className="bg-white border border-canopy-border rounded-lg shadow-xl p-3"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-canopy-text uppercase tracking-wide">
                {label}
              </span>
              <button
                type="button"
                className="text-xs text-canopy-textMuted hover:text-canopy-text"
                onClick={() => setIsOpen(false)}
              >
                Close
              </button>
            </div>

            <label className="flex items-center gap-2 text-sm font-medium mb-2">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={options.length > 0 && allSelected}
                onChange={toggleAll}
              />
              Select all
            </label>

            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search values..."
              className="w-full border border-canopy-border rounded px-2 py-1 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-canopy-blue"
            />

            <div className="max-h-48 overflow-auto border border-canopy-border rounded">
              {filteredOptions.length === 0 ? (
                <div className="px-2 py-3 text-sm text-canopy-textMuted text-center">
                  No matches
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <label
                    key={`${columnKey}-${option}`}
                    className="flex items-center gap-2 px-2 py-1 text-sm cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={draftSelection.has(option)}
                      onChange={() => toggleOption(option)}
                    />
                    <span className="truncate" title={option}>
                      {option}
                    </span>
                  </label>
                ))
              )}
            </div>

            <div className="flex items-center justify-between pt-3 text-xs">
              <button
                type="button"
                className="text-canopy-textMuted hover:text-canopy-text"
                onClick={handleClear}
              >
                Clear
              </button>
              <button
                type="button"
                className="text-canopy-blue hover:text-canopy-blueHover font-semibold"
                onClick={handleApply}
              >
                Apply
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const LeadList = ({
  userPermission,
  leads,
  loading,
  onLeadDeleted,
  searchTerm,
  totalLeads = 0,
  currentPage = 1,
  rowsPerPage = 50,
  onPageChange,
  onRowsPerPageChange,
  onFiltersChange,
}) => {
  const [activeTab, setActiveTab] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [salesRepValues, setSalesRepValues] = useState([]);
  const [pendingAntiquityMonth, setPendingAntiquityMonth] = useState("");
  const [urgencySort, setUrgencySort] = useState(null);
  const [statusSort, setStatusSort] = useState("desc");
  const hasApiLeads = Array.isArray(leads);
  const sourceLeads = useMemo(
    () => (hasApiLeads ? leads : []),
    [hasApiLeads, leads],
  );

  useEffect(() => {
    let cancelled = false;
    const loadLookups = async () => {
      try {
        const [salesReps] = await Promise.all([clientService.getSalesReps("")]);
        if (cancelled) return;
        setSalesRepValues(
          salesReps
            .map((rep) => normalizeCellValue(rep?.name))
            .filter((value) => value && value !== "—"),
        );
      } catch (error) {
        console.error("Failed to load lookup values", error);
      }
    };
    loadLookups();
    return () => {
      cancelled = true;
    };
  }, []);

  const salesRepTabs = useMemo(() => {
    const counts = new Map();
    const reps = new Set(salesRepValues);

    sourceLeads.forEach((lead) => {
      const rep = normalizeCellValue(getLeadDisplaySalesRep(lead));
      if (rep === "—") return;
      reps.add(rep);
      counts.set(rep, (counts.get(rep) || 0) + 1);
    });

    return [
      {
        id: "all",
        label: "All Leads",
        salesRep: null,
        count: sourceLeads.length,
      },
      ...Array.from(reps)
        .sort((a, b) => a.localeCompare(b))
        .map((rep) => ({
          id: `rep:${rep}`,
          label: rep,
          salesRep: rep,
          count: counts.get(rep) || 0,
        })),
    ];
  }, [sourceLeads, salesRepValues]);

  const tabScopedLeads = useMemo(() => {
    const active = salesRepTabs.find((t) => t.id === activeTab);
    if (!active?.salesRep) return sourceLeads;

    return sourceLeads.filter(
      (lead) =>
        normalizeCellValue(getLeadDisplaySalesRep(lead)) === active.salesRep,
    );
  }, [sourceLeads, salesRepTabs, activeTab]);

  const stats = useMemo(() => {
    const openLeads = tabScopedLeads.filter(
      (lead) => !["Closed", "Lost"].includes(getLeadStatusValue(lead)),
    ).length;
    const acceptanceLeads = tabScopedLeads.filter(
      (lead) => getLeadStatusValue(lead) === "Waiting on Acceptance",
    ).length;
    const closedDeals = tabScopedLeads.filter(
      (lead) => getLeadStatusValue(lead) === "Closed",
    ).length;
    const wonPercentage =
      tabScopedLeads.length === 0
        ? 0
        : (closedDeals * 100) / tabScopedLeads.length;
    const lostLeads = tabScopedLeads.filter(
      (lead) => getLeadStatusValue(lead) === "Lost",
    ).length;

    return [
      {
        id: "open",
        label: "Open",
        value: openLeads,
        helper: "All open leads",
        icon: PackageOpen,
        helperClass: "text-canopy-blue",
        iconClass: "text-canopy-blue",
        filterValues: [
          "Open",
          "Waiting for Documents",
          "Ready for Quote",
          "Waiting on Acceptance",
        ],
      },
      {
        id: "acceptance",
        label: "Waiting on Acceptance",
        value: acceptanceLeads,
        helper: "All waiting on acceptance leads",
        icon: ClockArrowUp,
        helperClass: "text-orange-600",
        iconClass: "text-orange-600",
        filterValues: ["Waiting on Acceptance"],
      },
      {
        id: "closed",
        label: "Closed",
        value: closedDeals,
        helper: "All closed deals",
        icon: Package,
        helperClass: "text-purple-600",
        iconClass: "text-purple-600",
        filterValues: ["Closed"],
      },
      {
        id: "closed",
        label: "Closed",
        value: `${Math.round(wonPercentage)}%`,
        helper: "All won leads percentage",
        icon: Trophy,
        helperClass: "text-green-600",
        iconClass: "text-green-600",
        filterValues: ["Closed"],
      },
      {
        id: "lost",
        label: "Lost",
        value: lostLeads,
        helper: "All lost leads",
        icon: BadgeX,
        helperClass: "text-red-600",
        iconClass: "text-red-600",
        filterValues: ["Lost"],
      },
    ];
  }, [tabScopedLeads]);

  useEffect(() => {
    const tabExists = salesRepTabs.some((tab) => tab.id === activeTab);
    if (!tabExists) setActiveTab("all");
  }, [salesRepTabs, activeTab]);

  const handleDeleteLead = async (leadId, leadName) => {
    // if (userPermission !== "masterAccess") {
    //   toast.error("You do not have permission to delete leads");
    //   return;
    // }
    if (
      window.confirm(
        `Are you sure you want to delete ${leadName}? This action cannot be undone.`,
      ) &&
      window.confirm(
        `Do you really want to delete ${leadName}? Click “Ok” only if you are absolutely sure.`,
      )
    ) {
      try {
        await leadService.deleteLead(leadId);
        onLeadDeleted(leadId);
        toast.success("Lead deleted successfully");
      } catch (error) {
        toast.error("Failed to delete lead");
        console.error("Delete error:", error);
      }
    }
  };

  const columnValueOptions = useMemo(() => {
    const optionSets = FILTERABLE_COLUMNS.reduce((acc, column) => {
      acc[column.key] = new Set();
      return acc;
    }, {});

    tabScopedLeads.forEach((lead) => {
      FILTERABLE_COLUMNS.forEach(({ key, accessor }) => {
        optionSets[key].add(accessor(lead));
      });
    });

    Object.entries(columnFilters).forEach(([key, values]) => {
      if (!values || values.length === 0) return;
      if (!optionSets[key]) optionSets[key] = new Set();
      values.forEach((value) => optionSets[key].add(value));
    });

    salesRepValues.forEach((value) => optionSets.salesRep?.add(value));
    return Object.fromEntries(
      Object.entries(optionSets).map(([key, set]) => [
        key,
        Array.from(set).sort((a, b) => a.localeCompare(b)),
      ]),
    );
  }, [tabScopedLeads, columnFilters, salesRepValues]);

  const areSameFilterValues = (a = [], b = []) =>
    a.length === b.length && a.every((value) => b.includes(value));

  const pendingStatusFilterValues = ["Waiting for Documents"];
  const isPendingStatusFilterActive = areSameFilterValues(
    columnFilters.status || [],
    pendingStatusFilterValues,
  );

  const pendingAntiquityMonthOptions = useMemo(() => {
    const offsets = new Set();
    tabScopedLeads.forEach((lead) => {
      if (getLeadStatusValue(lead) !== "Waiting for Documents") return;
      const createdDate = toValidDate(getLeadCreationDate(lead));
      if (!createdDate) return;
      const monthOffset = getMonthOffsetFromNow(createdDate);
      if (monthOffset >= 0) offsets.add(monthOffset);
    });

    return Array.from(offsets)
      .sort((a, b) => a - b)
      .map((offset) => ({
        value: String(offset),
        label: getAntiquityMonthLabel(offset),
      }));
  }, [tabScopedLeads]);

  const hasPendingAntiquityFilter =
    isPendingStatusFilterActive && pendingAntiquityMonth !== "";

  useEffect(() => {
    if (isPendingStatusFilterActive) return;
    setPendingAntiquityMonth("");
  }, [isPendingStatusFilterActive]);

  useEffect(() => {
    if (!pendingAntiquityMonth) return;
    const exists = pendingAntiquityMonthOptions.some(
      (option) => option.value === pendingAntiquityMonth,
    );
    if (!exists) setPendingAntiquityMonth("");
  }, [pendingAntiquityMonth, pendingAntiquityMonthOptions]);

  const filteredLeads = useMemo(() => {
    if (!tabScopedLeads.length) return tabScopedLeads;
    return tabScopedLeads.filter((lead) => {
      const matchesColumnFilters = Object.entries(columnFilters).every(
        ([key, values]) => {
          if (!values || values.length === 0) return true;
          const accessor = FILTERABLE_COLUMN_MAP[key]?.accessor;
          if (!accessor) return true;
          return values.includes(accessor(lead));
        },
      );

      if (!matchesColumnFilters) return false;
      if (!hasPendingAntiquityFilter) return true;

      const createdDate = toValidDate(getLeadCreationDate(lead));
      if (!createdDate) return false;

      return (
        getMonthOffsetFromNow(createdDate) === Number(pendingAntiquityMonth)
      );
    });
  }, [
    tabScopedLeads,
    columnFilters,
    hasPendingAntiquityFilter,
    pendingAntiquityMonth,
  ]);

  const sortedLeads = useMemo(() => {
    return [...filteredLeads].sort((a, b) => {
      const ra = STATUS_RANK[a?.lead?.status] ?? 6;
      const rb = STATUS_RANK[b?.lead?.status] ?? 6;
      const diff = statusSort === "desc" ? ra - rb : rb - ra;
      if (diff !== 0) return diff;

      if (urgencySort) {
        const ura = URGENCY_RANK[a?.lead?.urgency] ?? 3;
        const urb = URGENCY_RANK[b?.lead?.urgency] ?? 3;
        return urgencySort === "desc" ? ura - urb : urb - ura;
      }
      return 0;
    });
  }, [filteredLeads, urgencySort, statusSort]);

  const handleUrgencySortToggle = () => {
    setUrgencySort((prev) =>
      prev === null ? "desc" : prev === "desc" ? "asc" : null,
    );
  };

  const handleStatusSortToggle = () => {
    setStatusSort((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const handlePendingAntiquityMonthChange = (value) => {
    setPendingAntiquityMonth(value);
  };

  const handleClearPendingAntiquityMonth = () => {
    setPendingAntiquityMonth("");
  };

  const handleFilterApply = (columnKey, values) => {
    const next = { ...columnFilters };
    if (!values || values.length === 0) {
      delete next[columnKey];
    } else {
      next[columnKey] = values;
    }
    setColumnFilters(next);
    if (columnKey === "salesRep") {
      const serverFilters = {
        salesRep: next.salesRep,
      };
      onFiltersChange?.(serverFilters, { resetPage: true });
    }
  };

  const handleKpiClick = (filterValues) => {
    const activeStatusValues = columnFilters.status || [];
    const shouldClear = areSameFilterValues(activeStatusValues, filterValues);
    handleFilterApply("status", shouldClear ? null : filterValues);
  };

  const handleClearAllFilters = () => {
    setColumnFilters({});
    setPendingAntiquityMonth("");
    onFiltersChange?.({ salesRep: undefined }, { resetPage: true });
  };

  const activeFilterSummaries = Object.entries(columnFilters).map(
    ([key, values]) => ({
      key,
      label: FILTERABLE_COLUMN_MAP[key]?.label || key,
      values,
    }),
  );
  const pendingAntiquityFilterLabel =
    pendingAntiquityMonthOptions.find(
      (option) => option.value === pendingAntiquityMonth,
    )?.label || getAntiquityMonthLabel(Number(pendingAntiquityMonth || 0));
  const hasActiveFilters =
    activeFilterSummaries.length > 0 || hasPendingAntiquityFilter;
  const showFilteredEmptyState = hasActiveFilters && filteredLeads.length === 0;
  const effectiveTotalLeads = hasApiLeads ? totalLeads : filteredLeads.length;
  const visibleLeadCount = filteredLeads.length;
  const safeRows = rowsPerPage || 1;
  const totalPages =
    effectiveTotalLeads === 0
      ? 1
      : Math.max(1, Math.ceil(effectiveTotalLeads / safeRows));
  const rawPageStart =
    effectiveTotalLeads === 0 ? 0 : (currentPage - 1) * safeRows + 1;
  const displayPageStart =
    effectiveTotalLeads === 0 ? 0 : Math.min(rawPageStart, effectiveTotalLeads);
  const displayPageEnd =
    effectiveTotalLeads === 0
      ? 0
      : filteredLeads.length > 0
        ? Math.min(
            displayPageStart + filteredLeads.length - 1,
            effectiveTotalLeads,
          )
        : displayPageStart;

  const getVisiblePages = () => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, currentPage - half);
    let end = start + maxButtons - 1;
    if (end > totalPages) {
      end = totalPages;
      start = totalPages - maxButtons + 1;
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleExport = async () => {
    try {
      // if (userPermission !== "masterAccess") {
      //   toast.error("You do not have permission to export lead information");
      //   return;
      // }
      // Check later
      setExporting(true);
      const blob = await leadService.exportLeads({
        search: (searchTerm || "").trim() || undefined,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export ready");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading && !hasApiLeads) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="canopy-spinner mx-auto mb-4"></div>
          <p className="text-canopy-textMuted">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    // <div className="bg-white rounded-lg shadow-sm animate-fadeIn">
    <div className="space-y-6 animate-fadeIn">
      {/* KPIs */}
      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          {stats.map((item) => {
            const Icon = item.icon;
            const isActive = areSameFilterValues(
              columnFilters.status || [],
              item.filterValues || [],
            );
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleKpiClick(item.filterValues)}
                className={`text-left border rounded-xl p-5 bg-white transition-colors ${
                  isActive
                    ? "border-canopy-blue ring-2 ring-canopy-blue/10"
                    : "border-canopy-border hover:border-canopy-blue/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-canopy-text">
                    {item.label}
                  </h3>
                  <Icon className={`h-4 w-4 ${item.iconClass}`} />
                </div>
                <p className="text-4xl font-bold text-canopy-text mt-3">
                  {item.value}
                </p>
                <p className={`text-sm mt-2 ${item.helperClass}`}>
                  {item.helper}
                </p>
              </button>
            );
          })}
        </div>
        {isPendingStatusFilterActive && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-canopy-border pt-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-canopy-textMuted">
              Pending Antiquity
            </span>
            <label
              htmlFor="pending-antiquity-month"
              className="text-sm text-canopy-text"
            >
              Month
            </label>
            <select
              id="pending-antiquity-month"
              value={pendingAntiquityMonth}
              onChange={(e) =>
                handlePendingAntiquityMonthChange(e.target.value)
              }
              className="text-sm border border-canopy-border rounded px-2 py-1 bg-white"
              disabled={pendingAntiquityMonthOptions.length === 0}
            >
              <option value="">All months</option>
              {pendingAntiquityMonthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {pendingAntiquityMonth !== "" && (
              <button
                type="button"
                onClick={handleClearPendingAntiquityMonth}
                className="text-xs text-canopy-blue hover:text-canopy-blueHover font-medium"
              >
                Clear month
              </button>
            )}
          </div>
        )}
      </section>
      {/* Tabs */}
      <section className="bg-white border border-canopy-border rounded-xl p-3">
        <div className="canopy-tabs px-6 flex items-center gap-2">
          {salesRepTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                handleFilterApply(
                  "salesRep",
                  tab.salesRep ? [tab.salesRep] : null,
                );
              }}
              className={`canopy-tab ${activeTab === tab.id ? "all" : ""}`}
            >
              {tab.label}
            </button>
          ))}
          <div className="flex-1" />
          <div className="ml-auto flex items-center gap-3">
            <span className="px-2 py-0.5 text-xs bg-[#4f7cff] text-white rounded-full">
              {visibleLeadCount} {visibleLeadCount === 1 ? "lead" : "leads"}
              {hasApiLeads && effectiveTotalLeads !== visibleLeadCount
                ? ` of ${effectiveTotalLeads}`
                : ""}
            </span>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="px-3 py-1 text-xs inline-flex items-center disabled:opacity-60"
              title="Export all leads (filters applied)"
              aria-label="Export leads to Excel"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export to Excel
                </>
              )}
            </button>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="px-6 py-3 border-b border-canopy-border bg-gray-50">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-canopy-textMuted">
                Active Filters
              </span>
              {activeFilterSummaries.map(({ key, label, values }) => {
                const preview =
                  values.length === 0
                    ? "No values"
                    : values.length <= 2
                      ? values.join(", ")
                      : `${values.length} selected`;
                return (
                  <span
                    key={key}
                    className="inline-flex items-center gap-2 bg-white border border-canopy-border rounded-full px-3 py-1 text-xs text-canopy-text"
                  >
                    <span className="font-medium">{label}:</span>
                    <span
                      className="truncate max-w-[8.5rem]"
                      title={
                        values.length === 0
                          ? "No values selected"
                          : values.join(", ")
                      }
                    >
                      {preview}
                    </span>
                    <button
                      type="button"
                      className="text-canopy-textMuted hover:text-canopy-text"
                      onClick={() => handleFilterApply(key, null)}
                      aria-label={`Clear ${label} filter`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                );
              })}
              {hasPendingAntiquityFilter && (
                <span className="inline-flex items-center gap-2 bg-white border border-canopy-border rounded-full px-3 py-1 text-xs text-canopy-text">
                  <span className="font-medium">Month Antiquity:</span>
                  <span className="truncate max-w-[8.5rem]">
                    {pendingAntiquityFilterLabel}
                  </span>
                  <button
                    type="button"
                    className="text-canopy-textMuted hover:text-canopy-text"
                    onClick={handleClearPendingAntiquityMonth}
                    aria-label="Clear month antiquity filter"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              )}
              <button
                type="button"
                className="ml-auto text-xs text-canopy-blue hover:text-canopy-blueHover font-medium"
                onClick={handleClearAllFilters}
              >
                Clear all filters
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="canopy-table">
            <thead>
              <tr>
                <th>
                  <div className="flex items-center gap-2">
                    <span>Name</span>
                    <ColumnFilter
                      columnKey="name"
                      label="Name"
                      options={columnValueOptions.name || []}
                      selectedValues={columnFilters.name}
                      onApply={handleFilterApply}
                    />
                  </div>
                </th>
                <th>
                  <div className="flex items-center gap-2">
                    <span>Company / Contact</span>
                    <ColumnFilter
                      columnKey="company"
                      label="Company / Contact"
                      options={columnValueOptions.company || []}
                      selectedValues={columnFilters.company}
                      onApply={handleFilterApply}
                    />
                  </div>
                </th>
                <th>
                  <div className="flex items-center gap-2">
                    <span>Services</span>
                    <ColumnFilter
                      columnKey="services"
                      label="Services"
                      options={columnValueOptions.services || []}
                      selectedValues={columnFilters.services}
                      onApply={handleFilterApply}
                    />
                  </div>
                </th>
                <th>
                  <div className="flex items-center gap-2">
                    <span>Status</span>
                    <ColumnFilter
                      columnKey="status"
                      label="Status"
                      options={columnValueOptions.status || []}
                      selectedValues={columnFilters.status}
                      onApply={handleFilterApply}
                    />
                    <button
                      type="button"
                      onClick={handleStatusSortToggle}
                      aria-label="Sort by status"
                      title="Sorted by pipeline order"
                      className="p-1 rounded border border-transparent hover:border-canopy-border transition-colors text-canopy-blue"
                    >
                      {statusSort === "desc" ? (
                        <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUp className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </th>
                <th>
                  <div className="flex items-center gap-2">
                    <span>Email</span>
                    <ColumnFilter
                      columnKey="email"
                      label="Email"
                      options={columnValueOptions.email || []}
                      selectedValues={columnFilters.email}
                      onApply={handleFilterApply}
                    />
                  </div>
                </th>
                <th>
                  <div className="flex items-center gap-2">
                    <span>Phone Number</span>
                    <ColumnFilter
                      columnKey="phone"
                      label="Phone Number"
                      options={columnValueOptions.phone || []}
                      selectedValues={columnFilters.phone}
                      onApply={handleFilterApply}
                    />
                  </div>
                </th>
                <th>
                  <div className="flex items-center gap-2">
                    <span>Urgency</span>
                    <ColumnFilter
                      columnKey="urgency"
                      label="Urgency"
                      options={columnValueOptions.urgency || []}
                      selectedValues={columnFilters.urgency}
                      onApply={handleFilterApply}
                    />
                    <button
                      type="button"
                      onClick={handleUrgencySortToggle}
                      aria-label="Sort by urgency"
                      className={`p-1 rounded border border-transparent hover:border-canopy-border transition-colors ${
                        urgencySort
                          ? "text-canopy-blue"
                          : "text-canopy-textMuted"
                      }`}
                    >
                      {urgencySort === "desc" ? (
                        <ArrowDown className="h-4 w-4" />
                      ) : urgencySort === "asc" ? (
                        <ArrowUp className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </th>
                <th>
                  <div className="flex items-center gap-2">
                    <span>Sales Representative</span>
                    <ColumnFilter
                      columnKey="salesRep"
                      label="Sales Representative"
                      options={columnValueOptions.salesRep || []}
                      selectedValues={columnFilters.salesRep}
                      onApply={handleFilterApply}
                    />
                  </div>
                </th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {sortedLeads.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-12">
                    {showFilteredEmptyState ? (
                      <div className="text-canopy-textMuted">
                        <Filter className="mx-auto h-12 w-12 mb-4" />
                        <h3 className="text-lg font-medium text-canopy-text mb-2">
                          No leads match the selected filters
                        </h3>
                        <p className="mb-6">
                          Try adjusting or clearing your column filters to see
                          more results.
                        </p>
                        <button
                          type="button"
                          onClick={handleClearAllFilters}
                          className="canopy-btn-secondary inline-flex items-center gap-2"
                        >
                          <X className="h-4 w-4" />
                          Clear filters
                        </button>
                      </div>
                    ) : (
                      <div className="text-canopy-textMuted">
                        <Plus className="mx-auto h-12 w-12 mb-4" />
                        <h3 className="text-lg font-medium text-canopy-text mb-2">
                          No leads found
                        </h3>
                        <p className="mb-6">
                          Get started by creating your first lead or adjust your
                          search criteria.
                        </p>
                        <Link
                          to="/create_lead"
                          className="canopy-btn-primary inline-flex items-center"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add a Lead
                        </Link>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                sortedLeads.map((lead) => {
                  return (
                    <tr key={lead.id} className="group">
                      <td>
                        <Link
                          to={`/lead/${lead.id}`}
                          className="font-medium text-canopy-blue hover:text-canopy-blueHover"
                        >
                          {getLeadDisplayName(lead)}
                        </Link>
                      </td>
                      <td>
                        <span className="text-canopy-textMuted">
                          {getLeadDisplayCompanyName(lead)}
                        </span>
                      </td>
                      <td>
                        <ServiceTags lead={lead} />
                      </td>
                      <td>
                        <StatusTag lead={lead} />
                      </td>
                      <td>
                        <span className="text-canopy-textMuted">
                          {getLeadDisplayEmail(lead)}
                        </span>
                      </td>
                      <td>
                        <span className="text-canopy-textMuted">
                          {getLeadDisplayPhone(lead)}
                        </span>
                      </td>
                      <td>
                        <UrgencyTag lead={lead} />
                      </td>
                      <td>
                        <span className="text-canopy-textMuted">
                          {getLeadDisplaySalesRep(lead)}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Link
                            to={`/lead/${lead.id}`}
                            className="p-1 text-canopy-textMuted hover:text-canopy-blue rounded transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={() =>
                              handleDeleteLead(
                                lead.id,
                                getLeadDisplayName(lead),
                              )
                            }
                            className="p-1 text-canopy-textMuted hover:text-red-600 rounded transition-colors"
                            title="Delete Lead"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {effectiveTotalLeads > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-canopy-border">
            <div className="flex items-center space-x-2 text-sm text-canopy-textMuted">
              <span>
                Showing {displayPageStart}-{displayPageEnd} of{" "}
                {effectiveTotalLeads}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm text-canopy-textMuted hover:text-canopy-text disabled:opacity-40"
                aria-label="Previous page"
              >
                ←
              </button>
              {getVisiblePages().map((page) => (
                <button
                  key={page}
                  onClick={() => onPageChange?.(page)}
                  className={`px-3 py-1 text-sm rounded ${
                    page === currentPage
                      ? "bg-canopy-blue text-white"
                      : "text-canopy-textMuted hover:text-canopy-text"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() =>
                  onPageChange?.(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm text-canopy-textMuted hover:text-canopy-text disabled:opacity-40"
                aria-label="Next page"
              >
                →
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <select
                className="text-sm border border-canopy-border rounded px-2 py-1"
                value={rowsPerPage}
                onChange={(e) => {
                  onRowsPerPageChange?.(Number(e.target.value));
                }}
              >
                {[50, 100, 200].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="text-sm text-canopy-textMuted">per page</span>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default LeadList;
